import { useState, useCallback } from 'react';
import { trpClient } from '../client/trpClient';
import { useQuoteRecommendationRun } from './useQuoteRecommendationRun';
import { downloadBlob } from '../utils/download';
import { invalidateOrgCredits } from './useOrgCredits';

import { supplierSlug } from '../utils/supplierSlug';
import { displayRfqId } from '../utils/rfqIdentity';

type DownloadKind = 'sourcing-summary' | 'smart-bom';

/**
 * Export requests use responseType:'blob', so an error response's JSON body
 * arrives as a Blob — read it back out or the real server message is lost.
 */
const blobAwareErrorMessage = async (e: any, fallback: string): Promise<string> => {
    const data = e?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return parsed?.error?.message || parsed?.error || parsed?.message || fallback;
        } catch {
            return fallback;
        }
    }
    return data?.error?.message || data?.error || data?.message || e?.message || fallback;
};

export const useQuoteRecommendation = (rfqId?: string, longAfterMs?: number) => {
    const {
        result, setResult, isLoading, isGenerating, isLong, runError, dismissRunError, generate,
    } = useQuoteRecommendationRun(rfqId, longAfterMs);
    const [isAccepting, setIsAccepting] = useState<boolean>(false);
    // Holds the deliverable id in flight: a DownloadKind, or `supplier:<slug>`.
    const [downloading, setDownloading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const accept = useCallback(async (
        planComponents?: Array<{ componentId: string; allocations: Array<{ supplierName: string; sharePercent: number }> }>
    ) => {
        if (!rfqId || isAccepting) return;
        setIsAccepting(true);
        setError(null);
        try {
            setResult(await trpClient.acceptQuoteRecommendation(rfqId, planComponents));
            // Accepting sends award emails to winning suppliers — each is a
            // metered OUTREACH_MESSAGE charge, so refresh the balance chip.
            invalidateOrgCredits();
        } catch (e: any) {
            setError(e?.response?.data?.error || e.message || 'Failed to confirm the award plan.');
        } finally {
            setIsAccepting(false);
        }
    }, [rfqId, isAccepting, setResult]);

    const download = useCallback(async (kind: DownloadKind) => {
        if (!rfqId || downloading) return;
        setDownloading(kind);
        setError(null);
        try {
            if (kind === 'sourcing-summary') {
                downloadBlob(await trpClient.downloadSourcingSummary(rfqId), `${displayRfqId(rfqId)}-Sourcing-Summary.pdf`);
            } else {
                downloadBlob(await trpClient.downloadSmartBom(rfqId), `${displayRfqId(rfqId)}-SmartBOM.xlsx`);
            }
        } catch (e: any) {
            setError(await blobAwareErrorMessage(e, 'Download failed.'));
        } finally {
            setDownloading(null);
        }
    }, [rfqId, downloading]);

    const downloadSupplierRfq = useCallback(async (supplierName: string) => {
        if (!rfqId || downloading) return;
        const slug = supplierSlug(supplierName);
        setDownloading(`supplier:${slug}`);
        setError(null);
        try {
            downloadBlob(await trpClient.downloadSupplierRfq(rfqId, slug), `${displayRfqId(rfqId)}-RFQ-${slug}.pdf`);
        } catch (e: any) {
            setError(await blobAwareErrorMessage(e, 'Download failed.'));
        } finally {
            setDownloading(null);
        }
    }, [rfqId, downloading]);

    return {
        result,
        recommendation: result?.recommendation || null,
        componentContext: result?.componentContext || [],
        awardPlan: result?.awardPlan || null,
        isAccepted: Boolean(result?.awardPlan || result?.acceptedAt),
        isLoading,
        isGenerating,
        isLong,
        runError,
        dismissRunError,
        isAccepting,
        downloading,
        error,
        dismissError: () => setError(null),
        generate,
        accept,
        download,
        downloadSupplierRfq,
    };
};
